import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { AddBoxIcon, InfoIcon } from '@sharedCR/coreDesignSystem/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';

import {
  captionStyles,
  labelIconStyles,
  labelIconWrapperStyles,
  labelWrapperStyles,
  wrapperStyles,
} from '../AppraisalSurchargesCard/AppraisalSurchargesCard.styles';

import {
  ADD_CONTACT_BUTTON_TEXT,
  APPRAISAL_ORDER_CONTACTS_KEY,
  ORDER_CONTACTS_CAPTION,
  ORDER_CONTACTS_TITLE,
  ORDER_CONTACTS_COLUMNS,
  RESET_BUTTON_TEXT,
} from './AppraisalOrderContactsCard.constants';
import {
  addContactWrapperStyles,
  getCardStyles,
  getHeaderCellStyles,
  headerRowStyles,
  headerSpacerStyles,
  tableWrapperStyles,
} from './AppraisalOrderContactsCard.styles';
import { AppraisalOrderContactsCardTestIds } from './AppraisalOrderContactsCard.testIds';
import {
  addOtherOrderContact,
  buildInitialOrderContacts,
  buildOrderContactsLookup,
  checkIsAddOtherOrderContactAllowed,
  checkIsManualOption,
  checkIsOrderContactsDefault,
  clearOrderContact,
  getOrderContactRowsViewData,
  getPendingDeletedEntityIds,
  removeOrderContact,
  resetOrderContacts,
  saveManualOrderContact,
  selectOrderContactOption,
  setOrderContactDescription,
  setPrimaryOrderContact,
} from './AppraisalOrderContactsCard.utils';
import OrderContactRow from './components/OrderContactRow';

const defaultRows = [];
const defaultPendingChanges = {};
const defaultBorrowers = [];

const AppraisalOrderContactsCard = ({
  applicationData,
  entitySideData,
  setEntitySideData,
  setInitialEntitySideData,
  setIsSavingAllowed,
  setIsNestedEntityEditActive,
  onOpenAgentAndSeller,
  pendingChanges = defaultPendingChanges,
  agentsAndSellersPendingChanges = defaultPendingChanges,
  borrowers = defaultBorrowers,
  isAgentSlotsVisible = false,
  isEditAllowed = false,
  isOtherContactsEnabled = false,
  isHighlightDisabled = false,
  isLoading = false,
}) => {
  const [manualEntryRowId, setManualEntryRowId] = useState(null);

  const rows = entitySideData[APPRAISAL_ORDER_CONTACTS_KEY] ?? defaultRows;
  const savedContacts = applicationData.appraisalOrderContacts;
  const isSeeded = _.has(entitySideData, APPRAISAL_ORDER_CONTACTS_KEY);
  const isApplicationDataLoaded = !_.isEmpty(applicationData);
  const isManualEntryOpen = !_.isNil(manualEntryRowId);

  const lookup = useMemo(
    () =>
      buildOrderContactsLookup({
        borrowers,
        entitySideData,
        applicationData,
        pendingDeletedEntityIds: getPendingDeletedEntityIds(agentsAndSellersPendingChanges),
      }),
    [borrowers, entitySideData, applicationData, agentsAndSellersPendingChanges],
  );

  const rowsViewData = useMemo(
    () => getOrderContactRowsViewData({ rows, lookup, pendingChanges, isHighlightDisabled }),
    [rows, lookup, pendingChanges, isHighlightDisabled],
  );

  const isCardChanged = rowsViewData.some(({ isChanged }) => isChanged);
  const isResetVisible = isEditAllowed && !checkIsOrderContactsDefault(rows);
  const isAddContactVisible =
    isOtherContactsEnabled && isEditAllowed && checkIsAddOtherOrderContactAllowed(rows);
  const isActionDisabled = isLoading || isManualEntryOpen;

  const updateRows = useCallback(
    getUpdatedRows =>
      setEntitySideData(prevState => ({
        ...prevState,
        [APPRAISAL_ORDER_CONTACTS_KEY]: getUpdatedRows(
          prevState[APPRAISAL_ORDER_CONTACTS_KEY] ?? defaultRows,
        ),
      })),
    [setEntitySideData],
  );

  const onSelectOption = useCallback(
    ({ rowId, option }) => {
      if (checkIsManualOption(option)) {
        setManualEntryRowId(rowId);

        return;
      }

      updateRows(prevRows => selectOrderContactOption({ rows: prevRows, rowId, option, lookup }));
    },
    [lookup, updateRows],
  );

  const onClearContact = useCallback(
    rowId => updateRows(prevRows => clearOrderContact({ rows: prevRows, rowId })),
    [updateRows],
  );

  const onSetPrimary = useCallback(
    rowId => updateRows(prevRows => setPrimaryOrderContact({ rows: prevRows, rowId })),
    [updateRows],
  );

  const onRemoveContact = useCallback(
    rowId => updateRows(prevRows => removeOrderContact({ rows: prevRows, rowId })),
    [updateRows],
  );

  const onDescriptionChange = useCallback(
    ({ rowId, description }) =>
      updateRows(prevRows => setOrderContactDescription({ rows: prevRows, rowId, description })),
    [updateRows],
  );

  const onCancelManualEntry = useCallback(() => setManualEntryRowId(null), []);

  const onSubmitManualEntry = useCallback(
    ({ rowId, values }) => {
      updateRows(prevRows => saveManualOrderContact({ rows: prevRows, rowId, values }));
      setManualEntryRowId(null);
    },
    [updateRows],
  );

  const onReset = useCallback(() => updateRows(resetOrderContacts), [updateRows]);

  const onAddContact = useCallback(() => updateRows(addOtherOrderContact), [updateRows]);

  useEffect(() => {
    if (isSeeded || !isApplicationDataLoaded) {
      return;
    }

    const initialRows = buildInitialOrderContacts({
      savedContacts,
      isAgentSlotsVisible,
      isOtherContactsEnabled,
    });

    setEntitySideData(prevState => ({ ...prevState, [APPRAISAL_ORDER_CONTACTS_KEY]: initialRows }));
    setInitialEntitySideData(prevState => ({
      ...prevState,
      [APPRAISAL_ORDER_CONTACTS_KEY]: initialRows,
    }));
  }, [
    isSeeded,
    isApplicationDataLoaded,
    savedContacts,
    isAgentSlotsVisible,
    isOtherContactsEnabled,
    setEntitySideData,
    setInitialEntitySideData,
  ]);

  useEffect(() => {
    setIsSavingAllowed(!isManualEntryOpen);
    setIsNestedEntityEditActive(isManualEntryOpen);

    return () => {
      setIsSavingAllowed(true);
    };
  }, [isManualEntryOpen, setIsSavingAllowed, setIsNestedEntityEditActive]);

  return (
    <Box sx={wrapperStyles}>
      <Box sx={getCardStyles({ isFieldChanged: isCardChanged })}>
        <Box sx={labelWrapperStyles}>
          <Box sx={labelIconWrapperStyles}>
            <Box component={InfoIcon} size={IconSize[24]} sx={labelIconStyles} />
          </Box>
          <Typography component="h5" variant="bold16">
            {ORDER_CONTACTS_TITLE}
          </Typography>
          <Box sx={headerSpacerStyles} />
          {isResetVisible && (
            <Button
              data-testid={AppraisalOrderContactsCardTestIds.RESET_BUTTON}
              disabled={isActionDisabled}
              size="small"
              variant="line"
              onClick={onReset}
            >
              {RESET_BUTTON_TEXT}
            </Button>
          )}
        </Box>

        <Typography color="text.dark" component="p" sx={captionStyles} variant="regular12">
          {ORDER_CONTACTS_CAPTION}
        </Typography>

        <Box data-testid={AppraisalOrderContactsCardTestIds.CONTACTS_TABLE} sx={tableWrapperStyles}>
          <Box sx={headerRowStyles}>
            {ORDER_CONTACTS_COLUMNS.map(({ id, label, isCentered = false, isEditOnly = false }) => (
              <Box key={id} sx={getHeaderCellStyles({ isCentered })}>
                <Typography color="text.dark" component="p" variant="bold12">
                  {isEditOnly && !isEditAllowed ? '' : label}
                </Typography>
              </Box>
            ))}
          </Box>

          {rowsViewData.map((rowViewData, index) => (
            <OrderContactRow
              contact={rowViewData.contact}
              isActionDisabled={isActionDisabled}
              isAgentMissing={rowViewData.isAgentMissing}
              isChanged={rowViewData.isChanged}
              isEditAllowed={isEditAllowed}
              isLastRow={index === rowsViewData.length - 1}
              isLoading={isLoading}
              isManualEntryOpen={manualEntryRowId === rowViewData.row.id}
              key={rowViewData.row.id}
              options={rowViewData.options}
              row={rowViewData.row}
              rowKey={rowViewData.rowKey}
              slot={rowViewData.slot}
              onCancelManualEntry={onCancelManualEntry}
              onClearContact={onClearContact}
              onDescriptionChange={onDescriptionChange}
              onEditManualEntry={setManualEntryRowId}
              onOpenAgentAndSeller={onOpenAgentAndSeller}
              onRemoveContact={onRemoveContact}
              onSelectOption={onSelectOption}
              onSetPrimary={onSetPrimary}
              onSubmitManualEntry={onSubmitManualEntry}
            />
          ))}
        </Box>

        {isAddContactVisible && (
          <Box sx={addContactWrapperStyles}>
            <Button
              data-testid={AppraisalOrderContactsCardTestIds.ADD_CONTACT_BUTTON}
              disabled={isActionDisabled}
              size="medium"
              startIcon={<AddBoxIcon size={IconSize[20]} />}
              variant="outlined"
              onClick={onAddContact}
            >
              {ADD_CONTACT_BUTTON_TEXT}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

AppraisalOrderContactsCard.propTypes = {
  applicationData: PropTypes.object.isRequired,
  entitySideData: PropTypes.object.isRequired,
  setEntitySideData: PropTypes.func.isRequired,
  setInitialEntitySideData: PropTypes.func.isRequired,
  setIsSavingAllowed: PropTypes.func.isRequired,
  setIsNestedEntityEditActive: PropTypes.func.isRequired,
  onOpenAgentAndSeller: PropTypes.func.isRequired,
  pendingChanges: PropTypes.object,
  agentsAndSellersPendingChanges: PropTypes.object,
  borrowers: PropTypes.arrayOf(PropTypes.object),
  isAgentSlotsVisible: PropTypes.bool,
  isEditAllowed: PropTypes.bool,
  isOtherContactsEnabled: PropTypes.bool,
  isHighlightDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export default memo(AppraisalOrderContactsCard);
